import Grid from '@mui/material/Grid';
import { GridColDef } from '@mui/x-data-grid';
import { GridRenderCellParams, GridFilterModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ActionPermissions, CustomPermission, LabelValue, ListType, NewReportDataType, OverwriteReportDataType, PropReportDataType, ReportResponse } from '../../../@types';
import { PrintLabelEntityTypes } from '../../../@types/print-label.type';
import { Link } from '../../../components/atoms/Link';
import { DropdownItem } from '../../../components/atoms/SelectMenu';
import { Snackbar } from '../../../components/atoms/Snackbar';
import { CustomDataGrid } from '../../../components/layouts/CustomDataGrid';
import { PrintLabel } from '../../../components/molecules/PintLabel';
import { TagCell, TagCellItem } from '../../../components/molecules/TagCell';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { GridContainer } from '../../../styled/common.styles';
import { SavedReportTypeList, StructureTypeCategory, GridFeatureModeConstant } from '../../../configs/enums';
import { useGetStructurePaginatedDataByType } from '../../../queries/structure-query';
import { useGetStructureTypesByCategoryCode } from '../../../queries/structure-type-query';
import { isUserHasPermission } from '../../../configs/permissions';
import { useAddNewReport, useGetDefaultReportByReportType, useGetReportByCode, useUpdateReportByCode } from '../../../queries/report-query';
import { checkListPagetFilterAvailability, formatDate, formatDateOnly } from '../../../utils/common';
import { generateReportURL } from '../../../utils/report';
import { PLATFORM_NAME } from '../../../configs/common';
import { columnSelectionItems } from '../../../constants/structure';
import { useGetRegionTypes } from '../../../queries/regionTypes-query';
import { addNoneOptionToPresentationValues, getServicePresentationDescriptionFromValue, getUpdateStructurePageUrl } from '../../../utils/structure';

export const StructureList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const importSessionId = searchParams.get('importSessionId');
  const reportCode = searchParams.get('savedReportCode');
  const initialFilterModel = { field: 'importsessionId', operator: 'equals', value: importSessionId };
  
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [structureTypeList, setStructureTypeList] = useState<LabelValue[]>([]);
  const [reportName, setReportName] = useState('');
  const [reportColumnSelectionItems, setReportColumnSelectionItems] = useState<Array<ColumnSelectionItem>>();
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [newReportData, setNewReportData] = useState<PropReportDataType>();
  const [savedReportMessage, setSavedReportMessage] = useState('');
  const [reportViewCode, setReportViewCode] = useState<string>();
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [fetchDataGrid, setFetchDataGrid] = useState<boolean>(false);
  const [rowCountState, setRowCountState] = useState(0);
  const [structures, setStructures] = useState<Array<any>>([]);
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [firstLoad, setFirstLoad] = useState<boolean>(false);
  const [regionTypeList, setRegionTypeList] = useState<DropdownItem[]>([]);
  const [filterSearch, setFilterSearch] = useState(false);
  const [listId, setListId] = useState<string>(() => {
    if (reportCode) return `structure-list-${reportCode}`;
    if (importSessionId) return `structure-list-${importSessionId}`;
    return 'structure-list';
  });

  const useAddNewReportQuery = useAddNewReport();
  const useOverwriteReportQuery = useUpdateReportByCode(reportCode || '');
  const { data: reportQueryData, refetch: refetchGetReportByCodeQuery } = useGetReportByCode(reportCode || '');
  const defaultReportData = useGetDefaultReportByReportType(SavedReportTypeList.StructureList);
  const { data: structureTypeListData } = useGetStructureTypesByCategoryCode(StructureTypeCategory.Operational);
  const { data, isLoading, refetch: refetchStructureData, isRefetching } = useGetStructurePaginatedDataByType(
    StructureTypeCategory.Operational,
    { page, pageSize, filterModel, sortModel },
    reportColumnSelectionItems ? reportColumnSelectionItems : columnSelectionItems
  );
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const regionTypesQuery = useGetRegionTypes();

  useEffect(() => {
    if (regionTypesQuery.data) {
      const regionTypesItems = regionTypesQuery?.data?.map((item) => ({
        value: item.name,
        label: item.name
      })) || [];

      setRegionTypeList(regionTypesItems);
    }
  }, [regionTypesQuery.data]);

  useEffect(() => {
    setFirstLoad(true);
  }, []);

  useEffect(() => {
    if (reportCode) setListId(`structure-list-${reportCode}`);
    else if (importSessionId) setListId(`structure-list-${importSessionId}`);
    else setListId('structure-list');
  }, [reportCode]);

  useEffect(() => {
    if (!reportCode) {
      if (importSessionId) {
        setFilterModel({
          items: [initialFilterModel]
        });
      } else {
        setFilterModel({
          items: [{ field: 'isActive', operator: 'is', value: true }]
        });
      }
    }
  }, [reportCode]);

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    setColumns([
      {
        field: 'id',
        headerName: 'Node ID',
        width: 100,
        type: 'number',
        valueFormatter: (value: number) => value?.toString()
      },
      {
        field: 'name',
        headerName: 'Node Name',
        renderCell: (params: GridRenderCellParams) => 
          isUserHasPermission(ActionPermissions.Structure_Edit_View_Structure_Location, getUserPermissionsQuery.data) ?
            <Link href={getUpdateStructurePageUrl(location.pathname, params.row.id)}>{params.row?.name}</Link> :
            params.row.name
        ,
        valueGetter: (_value, row) => row?.name,
        width: 250
      },
      {
        field: 'nodeTypeName',
        headerName: 'Node Type',
        type: 'singleSelect',
        valueOptions: structureTypeList,
        valueGetter: (_value, row) => row?.nodeTypeName,
        width: 200
      },
      {
        field: 'mainAddress',
        headerName: 'Main Address',
        width: 300,
        valueGetter: (_value, row) => row?.mainAddress
      },
      {
        field: 'parentName',
        headerName: 'Parent Node Name',
        valueGetter: (_value, row) => row?.parentName,
        width: 250
      },
      {
        field: 'mainContact',
        headerName: 'Main Contact',
        width: 300,
        valueGetter: (_value, row) => row?.mainContact
      },
      {
        field: 'clientContractBrand',
        headerName: 'Client/Contract/Brand', 
        minWidth: 300,
        valueGetter: (_value, row) => row?.clientContractBrand
      },
      {
        field: 'client',
        headerName: 'Client', 
        minWidth: 200,
        valueGetter: (_value, row) => row?.client
      },
      {
        field: 'contract',
        headerName: 'Contract', 
        minWidth: 200,
        valueGetter: (_value, row) => row?.contract
      },
      {
        field: 'brand',
        headerName: 'Brand', 
        minWidth: 200,
        valueGetter: (_value, row) => row?.brand
      },
      {
        field: 'structureTags',
        headerName: 'Tags',
        renderCell: (params: GridRenderCellParams) => {
          if(params.row.structureTags)
            return (
              <TagCell tags={params.row.structureTags?.map((tag: TagCellItem) => tag)}/>
            );
          else
            return '';
        },
        valueGetter: (_value, row) => row.structureTags?.map((tag: TagCellItem) => tag.title).join(', '),
        width: 200
      },
      {
        field: 'notes',
        headerName: 'Notes',
        valueGetter: (_value, row) => row?.notes,
        width: 200
      },
      {
        field: 'referenceNo1',
        headerName: 'Reference Number 1', 
        width: 200,
        valueGetter: (_value, row) => row?.referenceNo1
      },
      {
        field: 'referenceNo2',
        headerName: 'Reference Number 2', 
        width: 200,
        valueGetter: (_value, row) => row?.referenceNo2
      },
      {
        field: 'referenceNo3',
        headerName: 'Reference Number 3', 
        width: 200,
        valueGetter: (_value, row) => row?.referenceNo3
      },
      {
        field: 'presentationValue',
        headerName: 'Presentation',
        type: 'singleSelect',
        width: 200,
        valueOptions: addNoneOptionToPresentationValues(),
        valueGetter: (_value, row) => row.presentationValue,
        valueFormatter: (value: string) => getServicePresentationDescriptionFromValue(value)
      },
      {
        field: 'presentationDate',
        headerName: 'Presentation Date',
        type: 'date',
        width: 200,
        valueGetter: (_value, row) => row.presentationDate && new Date(row.presentationDate),
        valueFormatter: (value: string) => formatDateOnly(value)
      },
      {
        field: 'siteSchema',
        headerName: 'Site Schema',
        width: 200,
        valueGetter: (_value, row) => row.siteSchema && row.siteSchema.name || ''
      },
      {
        field: 'regionType',
        headerName: 'Region Type',
        type: 'singleSelect',
        valueOptions: regionTypeList,
        valueGetter: (_value, row) => row?.regionType,
        width: 200
      },
      {
        field: 'zone',
        headerName: 'Zone',
        width: 200,
        valueGetter: (_value, row) => row.zone || ''
      },
      {
        field: 'allAddresses',
        headerName: 'All Addresses',
        width: 450,
        sortable: false,
        valueGetter: (_value, row) => row?.allAddresses,
        renderCell: (params: GridRenderCellParams) => (
          <div style={{ whiteSpace: 'pre-line' }}>
            {params.value}
          </div>
        )
      },
      {
        field: 'allContacts',
        headerName: 'All Contacts',
        sortable: false,
        width: 400,
        valueGetter: (_value, row) => row?.allContacts,
        renderCell: (params: GridRenderCellParams) => (
          <div style={{ whiteSpace: 'pre-line' }}>
            {params.value}
          </div>
        )
      },
      {
        field: 'tradingHrs',
        headerName: 'Trading Hours', 
        width: 300,
        valueGetter: (_value, row) => 
        {
          if (row.tradingHrs) {
            const tradingHrs = row.tradingHrs?.map((data: { tradingHrs: string }) => data.tradingHrs);
            return tradingHrs.join(', ');
          }
          return '';
        }
      },
      {
        field: 'isActive',
        headerName: 'Active',
        type: 'singleSelect',
        width: 200,
        valueOptions: [
          { value: 'any', label: 'Any' },
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' }
        ],
        valueGetter: (_value, row) => row?.isActive ? 'Yes' : 'No',
        valueFormatter: (value: boolean) => value
      },
      {
        field: 'parentNodeId',
        headerName: 'Parent Node ID',
        valueGetter: (_value, row) => row?.parentNodeId,
        width: 150,
        type: 'number',
        valueFormatter: (value: number) => value?.toString()
      },
      {
        field: 'parentNodeTypeName',
        headerName: 'Parent Node Type',
        type: 'singleSelect',
        valueOptions: structureTypeList,
        valueGetter: (_value, row) => row?.parentNodeTypeName,
        width: 150
      },
      {
        field: 'parentActive',
        headerName: 'Parent Active',
        type: 'singleSelect',
        valueOptions: [
          { value: 'any', label: 'Any' },
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' }
        ],
        valueFormatter: (_value, row) => row?.parentActive ? 'Yes' : 'No',
        width: 250
      },
      {
        field: 'directChildCount',
        headerName: '# of Children (direct)',
        width: 200,
        type: 'number',
        valueFormatter: (value: number) => value?.toString(),
        valueGetter: (_value, row) => row?.directChildCount,
        filterable: false,
      },
      {
        field: 'totalChildCount',
        headerName: '# of Children (total)',
        width: 200,
        type: 'number',
        valueFormatter: (value: number) => value?.toString(),
        valueGetter: (_value, row) => row?.totalChildCount,
        filterable: false,
      },
      {
        field: 'goodPartCount',
        headerName: 'Quantity Stock (Good)',
        width: 200,
        type: 'number',
        valueFormatter: (value: number) => value?.toString(),
        valueGetter: (_value, row) => row?.goodPartCount
      },
      {
        field: 'partCount',
        headerName: 'Quantity Stock (Total)',
        width: 200,
        type: 'number',
        valueFormatter: (value: number) => value?.toString(),
        valueGetter: (_value, row) => row?.partCount
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        type: 'date',
        width: 200,
        valueGetter: (_value, row) => new Date(row?.createdAt),
        valueFormatter: (value: string) => formatDate(value)
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        width: 200,
        valueGetter: (_value, row) => row?.createdBy
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        type: 'date',
        width: 200,
        valueGetter: (_value, row) => row?.updatedAt && new Date(row?.updatedAt),
        valueFormatter: (value: string) => formatDate(value)
      },
      {
        field: 'updatedBy',
        headerName: 'Updated By',
        width: 200,
        valueGetter: (_value, row) => row?.updatedBy
      },
      {
        field: 'importsessionId',
        headerName: 'Import Session ID',
        type:'string',
        width: 200,
        valueGetter: (_value, row) => row?.importsessionId || ''
      },
      {
        field: 'printLabel', 
        headerName: '', 
        renderCell: (params: GridRenderCellParams) => ( 
          <PrintLabel entityId={Number(params.row.id) || 0} entityType={PrintLabelEntityTypes.Structure}/>
        ),
        filterable: false
      }   
    ]);
  }, [permissions, structureTypeList, regionTypeList]);

  const onFilterChange = useCallback((newFilterModel: GridFilterModel) => {
    if (importSessionId && !newFilterModel.items.length) {
      setFilterModel({ items: [ initialFilterModel, ...newFilterModel.items ] });
    } else {
      if(newFilterModel.items.length)
        setFilterModel(newFilterModel);
      else 
        setFilterModel(reportCode ? { items: [] } : { items: [{ field: 'isActive', operator: 'is', value: true }] });
    }
  }, []);

  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setSortModel(sortModel);
  }, []);

  const handleSelectionModelChange = useCallback((selectionModel: GridRowSelectionModel) => {
    setSelectionModel(selectionModel);
  }, []);

  useEffect(() => {
    !reportCode && defaultReportData.refetch();
  }, [SavedReportTypeList.StructureList]);

  useEffect(() => {
    if (!reportCode && !importSessionId && defaultReportData.data && defaultReportData.data?.length > 0) {
      sessionStorage.setItem('structure-list', JSON.stringify(defaultReportData.data[0].dataGridProperties?.filterModel));
      setFilterModel(defaultReportData.data[0].dataGridProperties?.filterModel);
      setSortModel(defaultReportData.data[0].dataGridProperties?.sortModel || []);
      setReportColumnSelectionItems(defaultReportData.data[0].columnSelection);
      setPageSize(defaultReportData.data[0].dataGridProperties?.pageSize);

      if (defaultReportData.data[0].dataGridProperties?.columnOrder && columns) {
        const updatedColumns = defaultReportData.data[0].dataGridProperties?.columnOrder.map(orderCol => {
          const originalColumn = columns.find(col => col.field === orderCol.field);
          return originalColumn ? { ...originalColumn } : orderCol;
        });

        setColumns(updatedColumns);
      }
    }   
  }, [defaultReportData.data, reportCode]);

  useEffect(() => {
    if (reportCode) {
      refetchGetReportByCodeQuery();
      setReportViewCode(reportCode);
    }
  }, [reportCode, fetchDataGrid]);


  useEffect(() => {
    if (reportQueryData && firstLoad) {
      document.title = `${reportQueryData.name} (${reportCode}) | List Structure | ${PLATFORM_NAME}`;

      const storedFilterModel = sessionStorage.getItem(`structure-list-${reportCode}`);
      setFilterModel(storedFilterModel ? JSON.parse(storedFilterModel) : reportQueryData.dataGridProperties.filterModel);
      setSortModel(reportQueryData.dataGridProperties?.sortModel || []);
      setReportColumnSelectionItems(reportQueryData.columnSelection);
      setPageSize(reportQueryData.dataGridProperties?.pageSize);
      setReportName(reportQueryData.name);
      setIsDefault(reportQueryData.isDefault);

      if (reportQueryData.dataGridProperties?.columnOrder && columns) {
        const updatedColumns = reportQueryData.dataGridProperties?.columnOrder.map(orderCol => {
          const originalColumn = columns.find(col => col.field === orderCol.field);
          return originalColumn ? { ...originalColumn } : orderCol;
        });

        setColumns(updatedColumns);
      }

      setFirstLoad(false);
    }   
  }, [reportQueryData, reportViewCode, columns, firstLoad]);

  useEffect(() => {
    setNewReportData({
      dataGridProperties: {
        filterModel: filterModel,
        pageSize: pageSize,
        sortModel: sortModel || [],
        columnOrder: columns
      },
      reportTypeName: SavedReportTypeList.StructureList,
      reportName: reportName,
      isDefault: reportQueryData?.isDefault,
      id: reportQueryData?.id,
      reportOwner: reportQueryData?.createdBy,
      columnSelection: reportColumnSelectionItems
    });
  }, [filterModel, pageSize, isDefault, reportName, sortModel, columns]);

  useEffect(() => {
    if(filterModel.items.length === 0 || checkListPagetFilterAvailability(filterModel) || sortModel?.some(sort => sort.field)) {
      refetchStructureData();
    }
  }, [filterModel, sortModel, filterSearch]);

  useEffect(() => {
    if (page || pageSize || filterModel || sortModel || !reportCode || (reportCode && reportQueryData)) {
      refetchStructureData();
    }
  }, [page, pageSize, reportCode, isDefault, reportColumnSelectionItems, reportQueryData]);

  useEffect(() => {
    if (data) {
      setStructures(data?.data || []);
      setRowCountState(data?.total || 0);
    }
  }, [data]);

  useEffect(() => {
    if (structureTypeListData) {
      setStructureTypeList(structureTypeListData.map(listData => (
        { value: listData.name, label: listData.name }
      )));
    }
  }, [structureTypeListData]);

  const handleColumnsChange = (items: Array<ColumnSelectionItem>) => {
    setReportColumnSelectionItems(items);
  };

  const savedReport = async (newReportData: NewReportDataType) => {
    setSavedReportMessage('');
    const response: ReportResponse = await useAddNewReportQuery.mutateAsync(newReportData);
    setSavedReportMessage(response?.message ? response?.message : 'Successfully Created');
    const reportURL = generateReportURL(window.location.pathname, response.code);
    setOpenToast(true);
    navigate(reportURL, { replace: true });
  };

  const overwriteReport = async (overwriteReportData: OverwriteReportDataType) => {
    setSavedReportMessage('');
    const response: ReportResponse = await useOverwriteReportQuery.mutateAsync(overwriteReportData);
    setSavedReportMessage(response?.message ? response?.message : 'Successfully Updated');
    const reportURL = generateReportURL(window.location.pathname, response.code);
    setOpenToast(true); 
    navigate(reportURL, { replace: true });
  };

  return (
    <GridContainer>
      <Grid>
        <Snackbar  
          open={openToast} 
          autoHideDuration={4000} 
          message={savedReportMessage ? savedReportMessage : 'Successfully Created'}
          onClose={()=> setOpenToast(false)}
        />
      </Grid>
      <CustomDataGrid
        id={listId}
        columns={columns}
        rows={structures}
        exportFileName={ListType.StructureList}
        enableCSVExport={isUserHasPermission(ActionPermissions.Structure_Export, permissions)}
        columnSelectionItems={reportColumnSelectionItems ? reportColumnSelectionItems : columnSelectionItems}
        filterMode={GridFeatureModeConstant.server}
        onFilterModelChange={(filterModel: GridFilterModel) => {
          onFilterChange(filterModel);
        }}
        loading={isLoading || isRefetching}
        rowCount={rowCountState}
        rowsPerPageOptions={[25, 50, 100]}
        pagination={true}
        page={page}
        pageSize={pageSize}
        paginationMode={GridFeatureModeConstant.server}
        onPageChange={(newPage) => {
          setPage(newPage);
        }}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
        }}
        sortModel={sortModel}
        sortingMode={GridFeatureModeConstant.server}
        onSortModelChange={handleSortModelChange}
        selectionModel={selectionModel}
        onSelectionModelChange={handleSelectionModelChange}
        initialState={{
          columns: {
            columnVisibilityModel: {
              parentNodeId: false,
            },
          },
        }}
        componentsProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true }
          }
        }}
        structureTypeCategory={StructureTypeCategory.Operational}
        filterModel={filterModel}
        newReportData={newReportData} 
        handleSavedReport={(newReportData: NewReportDataType) => savedReport(newReportData)}
        handleOverwriteReport={(overwriteReportData: OverwriteReportDataType) => overwriteReport(overwriteReportData)}
        enableSavedReport={true}
        reportCode={reportViewCode}
        handleColumnsChange={handleColumnsChange}
        handleRefetch={() => setFetchDataGrid(!fetchDataGrid)}
        getRowHeight={() => 'auto'}
        setColumns={setColumns}
        filtersSearch={() => setFilterSearch(!filterSearch)}
      />
    </GridContainer>
  );
};